import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "../ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const cardBase =
  "rounded-xl p-5 flex flex-col gap-3 border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] hover:shadow-[0_0_20px_#00ff9044] transition-shadow";

const LEAVE_COLORS = {
  annual: "#60a5fa",
  sick: "#f87171",
  unpaid: "#fbbf24",
  maternity: "#c084fc",
  paternity: "#00ff90",
};

const LeaveApprovalPanel = ({ managerId }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/manager/leave/approvals`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to load leave requests"); return res.json(); })
      .then((data) => setRequests(Array.isArray(data) ? data : (data.requests || data?.data || [])))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [managerId]);

  const handleAction = (id, approved) => {
    setActionLoading(id);
    const token = localStorage.getItem("token");
    const action = approved ? "approve" : "reject";
    fetch(`${API_BASE}/api/leave/${id}/${action}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Action failed");
        setRequests((prev) => prev.filter((r) => r.id !== id));
        toast.success(approved ? "Leave approved" : "Leave rejected");
      })
      .catch((e) => toast.error(`Error: ${e.message}`))
      .finally(() => setActionLoading(null));
  };

  const typeMap = requests.reduce((acc, r) => {
    const t = (r.type || "other").toLowerCase();
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.entries(typeMap).map(([type, count]) => ({ type, count }));

  return (
    <div className={cardBase}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest">Leave Approvals</span>
        <span className="text-2xl">📝</span>
      </div>

      {loading && <p className="text-white/20 text-sm animate-pulse">Loading...</p>}
      {error && <p className="text-red-400 text-sm">⚠ {error}</p>}

      {!loading && !error && (
        <>
          {requests.length > 0 && (
            <div className="text-2xl font-bold text-white">
              {requests.length} <span className="text-base font-normal text-white/30">pending</span>
            </div>
          )}

          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={60}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                <XAxis dataKey="type" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 }} labelStyle={{ color: "#00ff90" }} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={LEAVE_COLORS[entry.type] || "#00ff90"} fillOpacity={0.8} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {requests.length > 0 ? requests.map((req) => (
              <div key={req.id} className="border border-[#00ff90]/10 bg-[#00ff90]/5 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-white text-sm font-medium">{req.name}</p>
                    <p className="text-xs text-white/30 mt-0.5">
                      <span style={{ color: LEAVE_COLORS[(req.type || "").toLowerCase()] || "#00ff90" }} className="capitalize">
                        {req.type}
                      </span>
                      {" · "}{req.dateRange}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => handleAction(req.id, true)}
                      disabled={actionLoading === req.id}
                      className="text-xs px-3 py-1 rounded-full bg-[#00ff90]/10 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/20 disabled:opacity-40 transition-colors"
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => handleAction(req.id, false)}
                      disabled={actionLoading === req.id}
                      className="text-xs px-3 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 disabled:opacity-40 transition-colors"
                    >
                      ✗
                    </button>
                  </div>
                </div>
              </div>
            )) : <p className="text-white/20 text-sm">No pending requests.</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default LeaveApprovalPanel;